import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { CreditCard, Loader2, Link2, Copy, MessageCircle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Payment-Link generator (/payment-link).
// Lets the admin/staff pick products + override the price manually, then
// generates a CardCom LowProfile credit-card URL they can copy / send
// via WhatsApp. No customer details, no order created in DB — pure
// "send a link, get paid" workflow.

const PaymentLink = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState({}); // { id: qty }
  const [customAmount, setCustomAmount] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { url, amount, label }

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  // Quantity helpers
  const setQty = (id, delta) => {
    setSelected(prev => {
      const cur = prev[id] || 0;
      const next = Math.max(0, cur + delta);
      const out = { ...prev };
      if (next === 0) delete out[id]; else out[id] = next;
      return out;
    });
  };

  // Auto-calculate subtotal from selected products. If admin entered a
  // manual amount, that overrides — useful for "round" promo prices or
  // custom deals.
  const items = Object.entries(selected).map(([id, qty]) => {
    const p = products.find(pr => pr.id === id);
    return p ? { ...p, quantity: qty } : null;
  }).filter(Boolean);
  const autoTotal = items.reduce((s, it) => s + it.price * it.quantity, 0);
  const manualNum = parseFloat(customAmount.replace(/[^0-9.]/g, ''));
  const finalAmount = Number.isFinite(manualNum) && manualNum > 0 ? manualNum : autoTotal;
  const productLabel = items.length > 0
    ? items.map(it => `${it.name} × ${it.quantity}`).join(' + ').slice(0, 50)
    : 'תשלום מבריאות הגבר';

  const handleGenerate = async () => {
    if (finalAmount <= 0) {
      toast.error('בחר מוצר או הזן סכום ידני');
      return;
    }
    setGenerating(true);
    try {
      const orderId = `PL-${Date.now().toString(36).toUpperCase()}`;
      const payRes = await axios.post(`${API}/payments/cardcom/create`, {
        order_id: orderId,
        amount: finalAmount,
        product_name: productLabel,
        customer_name: 'לקוח',
        customer_phone: '',
        customer_email: '',
        customer_address: '',
        success_url: 'https://menshealth.co.il/cart?payment=success',
        failure_url: 'https://menshealth.co.il/cart?payment=failed',
        webhook_url: `${API}/payments/cardcom/webhook`,
      });
      if (payRes.data?.payment_url) {
        setResult({ url: payRes.data.payment_url, amount: finalAmount, label: productLabel });
      } else {
        toast.error('נכשל ביצירת לינק');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה ביצירת לינק');
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('הועתק ללוח');
  };

  const whatsappMessage = result ? `שלום,\nלתשלום ההזמנה שלך:\n\n📦 ${result.label}\n💰 ${result.amount.toLocaleString()} ש"ח\n\nלחץ כאן לתשלום מאובטח:\n${result.url}\n\n✅ חשבונית מס/קבלה תישלח אוטומטית במייל\nבריאות הגבר` : '';

  const reset = () => {
    setResult(null);
    setSelected({});
    setCustomAmount('');
  };

  return (
    <div className="min-h-screen text-white" dir="rtl" style={{
      background: 'radial-gradient(circle at top right, #0a1d2b 0%, #050a0f 60%, #000 100%)',
      fontFamily: 'Heebo, system-ui, sans-serif',
    }} data-testid="payment-link-page">
      <Toaster richColors position="top-center" />
      <div className="max-w-md mx-auto px-4 pt-6 pb-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-700 flex items-center justify-center shadow-lg shadow-cyan-500/30">
            <Link2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight">לינק לתשלום</h1>
            <p className="text-[12px] text-cyan-300/80 font-bold">בריאות הגבר</p>
          </div>
        </div>

        {result ? (
          // Generated link panel — copy / share buttons
          <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-950/80 border border-emerald-500/40 rounded-3xl p-6 backdrop-blur-xl" data-testid="link-result">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-xl font-black text-center mb-1">הלינק מוכן!</h2>
            <p className="text-emerald-200/80 text-sm text-center mb-4">{result.label}</p>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-3 mb-4 text-center">
              <p className="text-[11px] text-amber-200/70">סכום לתשלום</p>
              <p className="text-4xl font-black text-amber-300" data-testid="link-amount">{result.amount.toLocaleString()} ₪</p>
            </div>
            <div className="bg-black/40 rounded-xl p-3 mb-3 break-all text-xs text-cyan-200" data-testid="link-url">
              {result.url}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button onClick={() => copy(result.url)} className="flex items-center justify-center gap-1 py-3 rounded-xl bg-white/10 hover:bg-white/20 font-bold text-sm" data-testid="copy-link-btn">
                <Copy className="w-4 h-4" /> העתק לינק
              </button>
              <a
                href={`whatsapp://send?text=${encodeURIComponent(whatsappMessage)}`}
                className="flex items-center justify-center gap-1 py-3 rounded-xl bg-green-500 hover:bg-green-600 font-bold text-sm"
                data-testid="share-wa-btn"
              >
                <MessageCircle className="w-4 h-4" /> שלח בווצאפ
              </a>
            </div>
            <button onClick={() => copy(whatsappMessage)} className="w-full py-2.5 rounded-xl bg-white/5 hover:bg-white/10 font-bold text-xs mb-2" data-testid="copy-message-btn">
              העתק הודעה מלאה
            </button>
            <button onClick={reset} className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 font-black transition-colors mt-2" data-testid="new-link-btn">
              לינק חדש
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Products picker */}
            <section className="bg-black/30 border border-white/10 rounded-2xl p-4 backdrop-blur-md" data-testid="link-products">
              <p className="text-cyan-300 text-xs font-bold mb-3 tracking-widest">בחירת מוצר (לא חובה)</p>
              <div className="space-y-2">
                {products.length === 0 && <p className="text-stone-400 text-sm">טוען מוצרים…</p>}
                {(() => {
                  // Same split logic as /quick-order — main products first,
                  // then a pink "פנק את בת הזוג" divider, then the partner
                  // (women's) products at the bottom.
                  const mainProds = products.filter(p => (p.category || '') !== 'partner');
                  const partnerProds = products.filter(p => (p.category || '') === 'partner');
                  const renderRow = (p) => {
                    const qty = selected[p.id] || 0;
                    return (
                      <div key={p.id} className={`flex items-center gap-2 p-2 rounded-xl border transition-all ${qty > 0 ? 'bg-cyan-500/10 border-cyan-500/40' : 'bg-white/5 border-white/10'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate" title={p.name}>{p.name}</p>
                          <p className="text-[11px] flex items-center gap-1.5">
                            {p.old_price && p.old_price > p.price && (
                              <span className="text-stone-400 line-through" data-testid={`pl-old-price-${p.id}`}>{p.old_price.toLocaleString()} ₪</span>
                            )}
                            <span className="text-cyan-300/90 font-bold" data-testid={`pl-price-${p.id}`}>{p.price.toLocaleString()} ₪</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => setQty(p.id, -1)} disabled={qty === 0} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 font-black" data-testid={`pl-minus-${p.id}`}>−</button>
                          <span className="w-7 text-center font-bold" data-testid={`pl-qty-${p.id}`}>{qty}</span>
                          <button type="button" onClick={() => setQty(p.id, +1)} className="w-8 h-8 rounded-lg bg-cyan-500 hover:bg-cyan-600 font-black" data-testid={`pl-plus-${p.id}`}>+</button>
                        </div>
                      </div>
                    );
                  };
                  return (
                    <>
                      {mainProds.map(renderRow)}
                      {partnerProds.length > 0 && (
                        <div className="pt-3 pb-1" data-testid="pl-partner-section-header">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-px bg-gradient-to-l from-pink-400/50 to-transparent" />
                            <p className="text-pink-300 text-xs font-bold tracking-widest">💗 פנק את בת הזוג</p>
                            <div className="flex-1 h-px bg-gradient-to-r from-pink-400/50 to-transparent" />
                          </div>
                        </div>
                      )}
                      {partnerProds.map(renderRow)}
                    </>
                  );
                })()}
              </div>
            </section>

            {/* Manual price override */}
            <section className="bg-black/30 border border-white/10 rounded-2xl p-4 backdrop-blur-md" data-testid="link-amount-section">
              <p className="text-cyan-300 text-xs font-bold mb-2 tracking-widest">מחיר ידני (דורס את חישוב המוצרים)</p>
              <input
                type="text"
                inputMode="decimal"
                value={customAmount}
                onChange={e => setCustomAmount(e.target.value)}
                placeholder={autoTotal > 0 ? `אוטומטי: ${autoTotal} ₪` : 'הכנס סכום בש"ח'}
                className="w-full bg-white/5 border border-white/15 focus:border-cyan-400 rounded-xl px-4 py-4 text-2xl text-center font-black outline-none placeholder-stone-500"
                data-testid="manual-amount-input"
              />
              {manualNum > 0 && autoTotal > 0 && manualNum !== autoTotal && (
                <p className="text-[11px] text-amber-300 mt-2 text-center">
                  ↺ הסכום הידני <strong>{manualNum.toLocaleString()} ₪</strong> דורס את חישוב המוצרים ({autoTotal.toLocaleString()} ₪)
                </p>
              )}
            </section>

            {/* Summary + generate */}
            <section className="bg-gradient-to-br from-cyan-500/10 to-blue-700/10 border border-cyan-500/30 rounded-2xl p-4">
              <div className="flex justify-between items-baseline">
                <span className="text-lg font-black text-cyan-200">סה״כ ללינק</span>
                <span className="text-3xl font-black text-cyan-300" data-testid="link-total">{finalAmount.toLocaleString()} ₪</span>
              </div>
            </section>

            <button
              onClick={handleGenerate}
              disabled={finalAmount <= 0 || generating}
              className="w-full py-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-700 hover:from-cyan-600 hover:to-blue-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-lg tracking-wider shadow-xl shadow-cyan-700/30 transition-all flex items-center justify-center gap-2"
              data-testid="generate-link-btn"
            >
              {generating ? <Loader2 className="w-6 h-6 animate-spin" /> : <CreditCard className="w-6 h-6" />}
              צור לינק תשלום אשראי
            </button>
          </div>
        )}

        <p className="text-center text-[10px] text-stone-500 mt-8">בריאות הגבר · יוצר לינקים · v1</p>
      </div>
    </div>
  );
};

export default PaymentLink;
